import { Api, Energy, UserWithEnergy } from "../handlers/types";
import { StorageService } from "./LocalStorageService";

export class EnergyService {
  private static api: Api<unknown>;
  private static energyData: Energy | null = null;
  private static updateTimer: NodeJS.Timeout | null = null;
  private static isRefreshing = false;

  /**
   * Initialize the energy system with API client
   * Should be called when the game starts
   */
  static initialize(api: Api<unknown>): void {
    this.api = api;

    // Load initial energy from user data
    const user = StorageService.loadUser() as UserWithEnergy;
    if (user?.energy) {
      this.energyData = user.energy;
      console.log(
        "Energy system initialized with value:",
        this.energyData.current
      );
    } else {
      console.warn("No energy data found in user, will refresh from API");
    }

    // Start energy updates
    this.startEnergyUpdates();
  }

  /**
   * Update energy data from a user object
   * Call this whenever a fresh user model is fetched from the backend
   */
  static updateFromUserModel(user: UserWithEnergy): void {
    if (user?.energy) {
      this.energyData = user.energy;
      console.log(
        "Energy data updated from user model:",
        this.energyData.current
      );

      // Restart energy update timer with new nextRestore value
      this.startEnergyUpdates();
    }
  }

  /**
   * Start a timer to refresh energy data based on nextRestore time
   */
  private static startEnergyUpdates(): void {
    // Clear any existing timer
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    // If no energy data yet, first fetch from API
    if (!this.energyData) {
      // TODO: Return if no energy data
      // this.refreshEnergyFromApi().then(() => {
      //   this.startEnergyUpdates();
      // });
      return;
    }

    const nextRestoreMs = this.energyData.nextRestore;

    // If energy is fully restored, no need for a timer
    if (!nextRestoreMs || this.energyData.current >= this.energyData.max) {
      console.log("Energy fully restored or no restore time available");
      return;
    }

    // Calculate time until next energy point (add small buffer to ensure we're after the restore time)
    const timeUntilNextRestore = Math.max(100, nextRestoreMs);
    console.log(`Next energy restore in ${timeUntilNextRestore}ms`);

    // Set timeout to update when next energy point is restored
    this.updateTimer = setTimeout(() => {
      console.log("Energy restore timeout triggered");
      this.refreshEnergyFromApi().then(() => {
        // After refreshing, setup the next timer
        this.startEnergyUpdates();
      });
    }, timeUntilNextRestore);
  }

  /**
   * Get the latest energy data from the API
   * This is an async method that returns a promise
   */
  static async refreshEnergyFromApi(): Promise<Energy | null> {
    // Prevent multiple simultaneous API calls
    if (this.isRefreshing || !this.api) {
      return this.energyData;
    }

    // Check if user exists before making API call
    const user = StorageService.loadUser();
    if (!user) {
      console.warn("No user found, skipping energy refresh from API");
      return this.energyData;
    }

    try {
      this.isRefreshing = true;
      const userWithEnergy = await this.api.api.usersList();

      // Update energy data from API response
      if (userWithEnergy?.energy) {
        this.energyData = userWithEnergy.energy;
      }

      return this.energyData;
    } catch (error) {
      console.error("Failed to refresh energy from API:", error);
      return this.energyData;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get the current energy level
   * Returns the cached value or falls back to a default
   */
  static getEnergy(): number {
    return this.energyData?.current ?? 0;
  }

  /**
   * Get the maximum possible energy
   */
  static getMaxEnergy(): number {
    return this.energyData?.max ?? 10;
  }

  /**
   * Get the time until next energy point restores (in seconds)
   */
  static getNextEnergyRestoreTime(): number {
    return this.energyData?.nextRestore
      ? Math.ceil(this.energyData.nextRestore / 1000)
      : 0;
  }

  /**
   * Get the time until energy is fully restored (in seconds)
   */
  static getFullEnergyRestoreTime(): number {
    return this.energyData?.fullRestore
      ? Math.ceil(this.energyData.fullRestore / 1000)
      : 0;
  }

  /**
   * Decrease energy by one and return the new value
   * This will update locally first, then sync with the API
   */
  static decreaseEnergy(): number {
    if (!this.energyData) {
      return 0;
    }

    // Update local state immediately for responsiveness
    const newEnergy = Math.max(0, this.energyData.current - 1);
    this.energyData.current = newEnergy;

    // Refresh from API to sync the server state
    setTimeout(
      () =>
        this.refreshEnergyFromApi().then(() => {
          // After refreshing, restart the timer with new restore time
          this.startEnergyUpdates();
        }),
      100
    );

    return newEnergy;
  }

  /**
   * Increase energy by a specific amount and return the new value
   * This will update locally first, then sync with the API
   */
  static increaseEnergy(amount: number): number {
    if (!this.energyData) {
      return 0;
    }

    // Update local state immediately for responsiveness
    const newEnergy = Math.min(
      this.energyData.max,
      this.energyData.current + amount
    );
    this.energyData.current = newEnergy;

    // Refresh from API to sync the server state
    setTimeout(
      () =>
        this.refreshEnergyFromApi().then(() => {
          // After refreshing, restart the timer with new restore time
          this.startEnergyUpdates();
        }),
      100
    );

    return newEnergy;
  }

  /**
   * Set energy to full and return the new value
   * This will update locally first, then sync with the API
   */
  static refillEnergyFull(): number {
    if (!this.energyData) {
      return 0;
    }

    // Update local state immediately for responsiveness
    this.energyData.current = this.energyData.max;

    // Refresh from API to sync the server state
    setTimeout(
      () =>
        this.refreshEnergyFromApi().then(() => {
          // After refreshing, restart the timer with new restore time
          this.startEnergyUpdates();
        }),
      100
    );

    return this.energyData.current;
  }

  /**
   * Check if there is enough energy to play
   */
  static hasEnergy(): boolean {
    return this.getEnergy() > 0;
  }

  /**
   * Clean up resources when the service is no longer needed
   * Should be called when the game is disposed
   */
  static dispose(): void {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    this.energyData = null;
  }
}
